import { ConfirmationDialog, PoolDropzone, PoolModal, PoolTypography } from '@synergy/components';
import { ErrorCode, FileRejection } from 'react-dropzone';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormControlLabel, FormHelperText, Stack, Switch, Theme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloFormSection } from '@synergy/components-palo';
import { ReactNode, useState } from 'react';
import { StarIcon } from '@synergy/theme';
import { fields } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { uploadableImageMediaTypes } from '@synergy/models';
import urljoin from 'url-join';

import * as CustomerDetailOverviewActions from '../store/customer-detail-overview-actions';
import { CustomerDetailOverviewFormValues } from '../models/customer-detail-overview-form-values';
import { PaloAccountLogo } from '../../../../components/palo-account-logo';
import { PaloSwitchLabel } from '../../../../../../common/components/palo/palo-switch-label';
import { customerSelector } from '../store/customer-detail-overview-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    validFileTypes: {
        marginBottom: theme.spacing(2),
    },
}));

/**
 * The section of the head: the logo, whether the member is a main contact,
 * and the grid of fields handed in (#2261).
 */
export const CustomerDetailOverviewBasicInfos = ({ children }: { children: ReactNode }) => {
    const classes = useStyles();
    const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const customer = useAppSelector(customerSelector);
    const apiBaseUrl = useAppSelector(state => state.settings.apiBaseUrl);
    const imageBaseUrl = useAppSelector(state => state.settings.imageCachePaths?.['184x79']);
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const fieldNames = fields<CustomerDetailOverviewFormValues>();

    const firstName =
        customer?.name && customer?.name !== ''
            ? customer?.name
            : intl.formatMessage({ id: 'app.contactsAndOrganizations.customer.new' });

    const handleProfileImageUpload =
        (form: FormikProps<CustomerDetailOverviewFormValues>) =>
        (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            if (rejectedFiles.length > 0) {
                if (rejectedFiles[0].errors[0].code === ErrorCode.FileInvalidType) {
                    form.setFieldError('image', 'app.error.invalidFileType');
                } else {
                    form.setFieldError('image', 'app.error.maxOneFile');
                }

                return;
            }

            dispatch(CustomerDetailOverviewActions.uploadProfileImage(acceptedFiles[0]));
            setIsUploadModalVisible(false);
        };

    const handleDeleteConfirm = () => {
        dispatch(CustomerDetailOverviewActions.removeProfileImage());
        setIsDeleteModalVisible(false);
    };

    const profileBaseSrc = apiBaseUrl && imageBaseUrl && urljoin(apiBaseUrl, imageBaseUrl);

    // The logo and the switches are the section's own settings, so they ride
    // in the section head rather than in a heading row of their own.
    const settings = (
        <Stack gap={2} flexDirection="row" alignItems="flex-start">
            <Field name={fieldNames.image}>
                {({ field }: FieldProps<string>) => (
                    <PaloAccountLogo
                        name={firstName}
                        src={field.value && profileBaseSrc ? urljoin(profileBaseSrc, field.value) : undefined}
                        disabled={!customer?.id}
                        onAdd={() => setIsUploadModalVisible(true)}
                        onRemove={() => setIsDeleteModalVisible(true)}
                    />
                )}
            </Field>
            <Field name={fieldNames.requireOrderNumber}>
                {({ field }: FieldProps<boolean>) => (
                    <FormControlLabel
                        control={<Switch {...field} color="primary" checked={field.value} name={field.name} />}
                        label={
                            <PaloSwitchLabel>
                                <FormattedMessage id="app.common.requireOrderNumber" />
                            </PaloSwitchLabel>
                        }
                        sx={{ marginRight: 0 }}
                    />
                )}
            </Field>
            <Field name={fieldNames.isMain}>
                {({ field }: FieldProps<boolean>) => (
                    <FormControlLabel
                        control={<Switch {...field} color="primary" checked={field.value} name={field.name} />}
                        label={
                            <PaloSwitchLabel>
                                <StarIcon />
                                <FormattedMessage id="app.common.mainContact" />
                            </PaloSwitchLabel>
                        }
                        sx={{ marginRight: 0 }}
                    />
                )}
            </Field>
        </Stack>
    );

    return (
        <PaloFormSection
            title={<FormattedMessage id="app.contactsAndOrganizations.detail.headTitle" />}
            action={settings}
        >
            {children}

            <PoolModal
                isVisible={isUploadModalVisible}
                title={<FormattedMessage id="app.contactsAndOrganizations.detail.uploadModal" />}
                handleClose={() => setIsUploadModalVisible(false)}
            >
                <Field name={fieldNames.image}>
                    {({ form, meta }: FieldProps<string>) => (
                        <>
                            <PoolTypography
                                typography="body2"
                                id="app.contactsAndOrganizations.detail.validFileTypes"
                                className={classes.validFileTypes}
                            />
                            <PoolDropzone
                                onDrop={handleProfileImageUpload(form)}
                                accept={uploadableImageMediaTypes}
                                multiple={true}
                                maxFiles={1}
                                useFsAccessApi={false} // https://geeksrepos.com/react-dropzone/react-dropzone/issues/1190
                            />
                            {meta.error && (
                                <FormHelperText error={true}>
                                    <FormattedMessage id={meta.error} />
                                </FormHelperText>
                            )}
                        </>
                    )}
                </Field>
            </PoolModal>
            <ConfirmationDialog
                isVisible={isDeleteModalVisible}
                title={<FormattedMessage id="app.contactsAndOrganizations.detail.deleteLogo" />}
                action={<FormattedMessage id="app.common.delete" />}
                onCancel={() => setIsDeleteModalVisible(false)}
                onConfirm={handleDeleteConfirm}
            >
                <FormattedMessage id="app.contactsAndOrganizations.detail.deleteLogoMessage" />
            </ConfirmationDialog>
        </PaloFormSection>
    );
};
